import { useMemo, useRef, useState } from "react";
import { Ico } from "../../components/icons/icons.jsx";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { CreateListEmptyState } from "../../components/emptyStates/CreateListEmptyState.jsx";
import { SearchField } from "../../components/ui/SearchField.jsx";
import { getAccountsCardLift } from "../../components/ui/cardStyles.js";
import { toolbarPrimaryButton } from "../../components/ui/buttonStyles.js";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { AccountCard, AccountActionMenu } from "./AccountCard.jsx";
import { STATUS_TINT, TIER_TINT } from "./accountConstants.js";

export function AccountsListPanel({ accounts, allAccounts, search, onSearchChange, onAdd, onOpen, onEdit, onDelete, loading }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const { requestDelete, deleteModalProps } = useDeleteConfirm();
  const [view, setView] = useState("grid");
  const [openMenuId, setOpenMenuId] = useState(null);

  const cardLift = getAccountsCardLift(dark);

  const stats = useMemo(() => {
    const active = allAccounts.filter((x) => x.status === "active").length;
    const trial = allAccounts.filter((x) => x.status === "trial").length;
    return { total: allAccounts.length, active, trial };
  }, [allAccounts]);

  const handleDeleteRequest = (account) => {
    requestDelete({
      title: "Delete account",
      description: "This account and all associated data will be permanently removed.",
      confirmText: account.companyName,
      entityLabel: "Account name",
      confirmLabel: "Delete account",
      onConfirm: () => onDelete(account.id),
    });
  };

  const listEmpty = !loading && allAccounts.length === 0;
  const searchEmpty = !listEmpty && accounts.length === 0;

  if (listEmpty) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <CreateListEmptyState
          title="No accounts yet"
          hint="Create your first account to manage company details, billing tier, and primary contacts."
          primaryAction={{ label: "Create account", onClick: onAdd }}
          c={c}
          dark={dark}
          A={A}
        />
        <DeleteConfirmModal {...deleteModalProps} />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <AccountsToolbar search={search} onSearchChange={onSearchChange} view={view} onViewChange={setView} onAdd={onAdd} c={c} A={A} />

      <div style={{ flex: 1, overflow: "auto", padding: 18, background: c.bg }}>
        <AccountsListHeader stats={stats} c={c} cardLift={cardLift} A={A} />

        {searchEmpty ? (
          <CreateListEmptyState title="No matching accounts" hint="Try another search term or clear the search field above." c={c} dark={dark} A={A} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                onOpen={onOpen}
                onEdit={onEdit}
                onDelete={handleDeleteRequest}
                menuOpen={openMenuId === account.id}
                onMenuToggle={(id) => setOpenMenuId(id === openMenuId ? null : id)}
              />
            ))}
          </div>
        ) : (
          <AccountsTable
            accounts={accounts}
            onOpen={onOpen}
            onEdit={onEdit}
            onDelete={handleDeleteRequest}
            openMenuId={openMenuId}
            onMenuToggle={setOpenMenuId}
            c={c}
            dark={dark}
            cardLift={cardLift}
          />
        )}
      </div>

      <DeleteConfirmModal {...deleteModalProps} />
    </div>
  );
}

function AccountsToolbar({ search, onSearchChange, view, onViewChange, onAdd, c, A }) {
  const viewBtn = (mode, icon, label) => {
    const on = view === mode;
    return (
      <button type="button" onClick={() => onViewChange(mode)} aria-label={label} aria-pressed={on} title={label} style={{ width: 38, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: on ? A : "transparent", color: on ? "#fff" : c.mid, borderRadius: 7, cursor: "pointer" }}>
        {icon}
      </button>
    );
  };

  return (
    <div style={{ padding: "12px 16px", borderBottom: `1px solid ${c.sbBdr}`, background: c.sb, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <SearchField value={search} onChange={onSearchChange} placeholder="Search accounts by name, city, or contact…" variant="hub" wrapperStyle={{ flex: 1, minWidth: 220, maxWidth: 420 }} />
      <div style={{ flex: 1, minWidth: 8 }} />
      <div style={{ display: "flex", gap: 3, padding: 3, height: 38, boxSizing: "border-box", background: c.hover, border: `1px solid ${c.sbBdr}`, borderRadius: 10 }}>
        {viewBtn("grid", Ico.grid, "Grid view")}
        {viewBtn("table", Ico.list, "Table view")}
      </div>
      <button type="button" onClick={onAdd} style={toolbarPrimaryButton(A)}>
        {Ico.plus}
        Create account
      </button>
    </div>
  );
}

function AccountsListHeader({ stats, c, cardLift, A }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: c.text }}>Accounts</h2>
        <p style={{ marginTop: 6, fontSize: 12.5, color: c.sub }}>Manage tenant accounts, billing, and contacts. Select a card to view details.</p>
      </div>
      <div style={{ display: "flex", alignItems: "stretch", background: c.card, border: `1px solid ${c.cardBdr}`, borderRadius: 12, boxShadow: cardLift, overflow: "hidden" }}>
        {[
          { label: "Total", val: stats.total, tint: A },
          { label: "Active", val: stats.active, tint: "#22C55E" },
          { label: "Trial", val: stats.trial, tint: "#0284C7" },
        ].map((s, i) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", borderLeft: i === 0 ? "none" : `1px solid ${c.cardBdr}` }}>
            <span style={{ width: 9, height: 9, borderRadius: 99, background: s.tint }} />
            <span style={{ fontSize: 24, fontWeight: 800, color: c.text, lineHeight: 1 }}>{s.val}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: c.sub, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountsTable({ accounts, onOpen, onEdit, onDelete, openMenuId, onMenuToggle, c, dark, cardLift }) {
  return (
    <div style={{ borderRadius: 14, border: `1px solid ${c.cardBdr}`, background: c.card, boxShadow: cardLift, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
              {["Status", "Company", "Tier", "Location", "Fleet", "MRR", "Contact", "Created", ""].map((h, i) => (
                <th key={h || i} style={{ textAlign: i === 8 ? "right" : "left", padding: "11px 16px", fontSize: 11, color: c.sub, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `1px solid ${c.cardBdr}`, whiteSpace: "nowrap" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map((account, idx) => {
              const statusTint = STATUS_TINT[account.status] ?? "#94A3B8";
              const tierTint = TIER_TINT[account.tier] ?? c.mid;
              const rowBg = idx % 2 === 0 ? "transparent" : dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
              const menuOpen = openMenuId === account.id;
              return (
                <tr key={account.id} onClick={() => onOpen(account)} style={{ background: rowBg, borderBottom: `1px solid ${c.cardBdr}`, cursor: "pointer" }} onMouseEnter={(e) => { e.currentTarget.style.background = c.hover; }} onMouseLeave={(e) => { e.currentTarget.style.background = rowBg; }}>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 99, background: statusTint }} />
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 700, color: c.text }}>{account.companyName}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 600, color: tierTint, textTransform: "capitalize" }}>{account.tier}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12.5, color: c.mid }}>{account.city}, {account.state}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12.5, color: c.text }}>{account.fleetSize}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12.5, color: c.text }}>₹{account.mrr.toLocaleString("en-IN")}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12.5, color: c.mid }}>{account.contact?.email ?? "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12.5, color: c.mid, whiteSpace: "nowrap" }}>{account.createdAt}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <AccountTableRowActions
                      account={account}
                      menuOpen={menuOpen}
                      onMenuToggle={onMenuToggle}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      c={c}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccountTableRowActions({ account, menuOpen, onMenuToggle, onEdit, onDelete, c }) {
  const menuBtnRef = useRef(null);

  return (
    <>
      <button
        ref={menuBtnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMenuToggle(menuOpen ? null : account.id);
        }}
        style={{ border: `1px solid ${c.sbBdr}`, background: c.hover, borderRadius: 8, width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: c.mid }}
        aria-label="Actions"
      >
        {Ico.moreVertical}
      </button>
      {menuOpen ? (
        <AccountActionMenu account={account} onEdit={onEdit} onDelete={onDelete} onClose={() => onMenuToggle(null)} anchorRef={menuBtnRef} />
      ) : null}
    </>
  );
}
